import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { isEqual } from "lodash"

import TextField from "@/components/form/textField/TextField"
import { useCalculateUnitQuery, useUnitQuery } from "@/hooks/api/useSettingQuery"
import { COD, ReadCODActionResponse } from "@/types/setting"
import { ActionSettingType, SquareUnit, UnitType } from "@/constants/unit"
import {
  DEFAULT_MIN_COD_ACCEL,
  DEFAULT_MAX_COD_ACCEL,
  DEFAULT_LOW_COD_ACCEL,
  DEFAULT_MEDIUM_COD_ACCEL,
  DEFAULT_HIGH_COD_ACCEL,
} from "@/app/settings/metric/actions/constants"
import { createActionSettingState } from "@/stores/settings"

export default function CODActionTab({
  initialData,
  editedData,
  setEditedData,
}: {
  initialData: ReadCODActionResponse | undefined
  editedData: ReadCODActionResponse | null
  setEditedData: (state: ReadCODActionResponse) => void
}) {
  const { t } = useTranslation()
  const { setIsAllValid, setHasChanges } = createActionSettingState()

  const { data: unit } = useUnitQuery()
  const { data: calcMin } = useCalculateUnitQuery({
    type: UnitType.ACCELERATION,
    value: DEFAULT_MIN_COD_ACCEL,
  })
  const { data: calcMax } = useCalculateUnitQuery({
    type: UnitType.ACCELERATION,
    value: DEFAULT_MAX_COD_ACCEL,
  })

  const [codValue, setCodValue] = useState<COD>({
    lowCODAccel: DEFAULT_LOW_COD_ACCEL,
    mediumCODAccel: DEFAULT_MEDIUM_COD_ACCEL,
    highCODAccel: DEFAULT_HIGH_COD_ACCEL,
    minCODAccel: calcMin?.result || DEFAULT_MIN_COD_ACCEL,
    maxCODAccel: calcMax?.result || DEFAULT_MAX_COD_ACCEL,
  })
  const [lastTouched, setLastTouched] = useState<"low" | "medium" | "high" | undefined>()

  useEffect(() => {
    const newValue = editedData?.global || initialData?.global
    if (newValue) {
      setCodValue((prev) => {
        if (isEqual(prev, newValue)) {
          return prev
        }

        return newValue
      })
    }
  }, [initialData, editedData])

  useEffect(() => {
    if (!initialData) {
      return
    }

    const hasChanged = !isEqual(initialData.global, codValue)
    setHasChanges(ActionSettingType.COD, hasChanged)
  }, [initialData, codValue])

  useEffect(() => {
    setIsAllValid(ActionSettingType.COD, !isErr())
  }, [codValue])

  const isErrThreshold = (type: "low" | "medium" | "high", value: number) => {
    const isUnderMin = value < (calcMin?.result || DEFAULT_MIN_COD_ACCEL)
    const isOverMax = value > (calcMax?.result || DEFAULT_MAX_COD_ACCEL)

    switch (type) {
      case "low":
        return isUnderMin || isOverMax || value >= codValue.mediumCODAccel
      case "medium":
        return (
          isUnderMin || isOverMax || value >= codValue.highCODAccel || value <= codValue.lowCODAccel
        )
      case "high":
        return isUnderMin || isOverMax || value <= codValue.mediumCODAccel
      default:
        return false
    }
  }

  const isErr = () => {
    const isEmpty = !codValue.lowCODAccel || !codValue.mediumCODAccel || !codValue.highCODAccel

    return (
      isEmpty ||
      isErrThreshold("low", codValue.lowCODAccel) ||
      isErrThreshold("medium", codValue.mediumCODAccel) ||
      isErrThreshold("high", codValue.highCODAccel)
    )
  }

  const getErrMessage = (type: "low" | "medium" | "high", value: number) => {
    const isUnderMin = value < (calcMin?.result || DEFAULT_MIN_COD_ACCEL)
    const isOverMax = value > (calcMax?.result || DEFAULT_MAX_COD_ACCEL)

    if (isUnderMin) {
      return t("rules.min", {
        name: `${t(`${type}CODAccel`)}`,
        min: calcMin?.result || DEFAULT_MIN_COD_ACCEL,
      })
    }
    if (isOverMax) {
      return t("rules.max", {
        name: `${t(`${type}CODAccel`)}`,
        max: calcMax?.result || DEFAULT_MAX_COD_ACCEL,
      })
    }

    switch (type) {
      case "low":
        if (codValue.lowCODAccel > codValue.mediumCODAccel) {
          return t("rules.min", {
            name: `${t("metric.lowCODAccel")}`,
            min: codValue.mediumCODAccel,
          })
        }
        break
      case "medium":
        if (codValue.mediumCODAccel > codValue.highCODAccel) {
          return t("rules.max", {
            name: `${t("metric.mediumCODAccel")}`,
            max: codValue.highCODAccel,
          })
        }
        if (codValue.mediumCODAccel < codValue.lowCODAccel) {
          return t("rules.min", {
            name: `${t("metric.mediumCODAccel")}`,
            min: codValue.lowCODAccel,
          })
        }
        break
      case "high":
        if (codValue.highCODAccel < codValue.mediumCODAccel) {
          return t("rules.min", {
            name: `${t("metric.highCODAccel")}`,
            min: codValue.mediumCODAccel,
          })
        }
        break
      default:
        return ""
    }

    if (codValue.lowCODAccel < DEFAULT_MIN_COD_ACCEL) {
      return t("rules.min", {
        name: `${t("cod")} ${t("threshold")}`,
        min: DEFAULT_MIN_COD_ACCEL,
      })
    } else if (codValue.lowCODAccel > DEFAULT_MAX_COD_ACCEL) {
      return t("rules.max", {
        name: `${t("cod")} ${t("threshold")}`,
        max: DEFAULT_MAX_COD_ACCEL,
      })
    } else {
      return ""
    }
  }

  const handleChange = (field: keyof COD, value: number) => {
    const newCodValue = {
      ...codValue,
      [field]: value,
    }
    setCodValue(newCodValue)
    setEditedData({ global: newCodValue })
  }

  const handleBlur = (field: keyof COD, value: number) => {
    const roundedValue = Math.round(value * 100) / 100
    const newCodValue = {
      ...codValue,
      [field]: roundedValue,
    }
    setCodValue(newCodValue)
    setEditedData({ global: newCodValue })
    setLastTouched(undefined)
  }

  const getAccelUnit = () => {
    const accelUnit = unit?.find((un) => un.type === UnitType.ACCELERATION)?.unit
    return accelUnit ? SquareUnit[accelUnit] || accelUnit : ""
  }

  return (
    <>
      <div className="ActionRowCont">
        <div className="row-title-editable">
          <p className="row-title-default-label">{`${t(`cod`)} ${t("effort")} 1`}</p>
          <div className="row-title-label">
            <p>{t("metric.lowCODAccel")}</p>
          </div>
        </div>

        <div className="ActionRowItemCont">
          <div className="action-threshold">
            <p className="action-form-label">
              {t("threshold")} ({getAccelUnit()})
            </p>
            <div className="jump-threshold-row">
              <TextField
                type="number"
                slotProps={{
                  htmlInput: {
                    step: 0.01,
                  },
                }}
                value={codValue.lowCODAccel}
                onChange={(e) => {
                  handleChange("lowCODAccel", Number(e.target.value))
                  if (isErrThreshold("low", Number(e.target.value))) {
                    setLastTouched("low")
                  }
                }}
                onBlur={(e) => handleBlur("lowCODAccel", Number(e.target.value))}
                onFocusCapture={() => setLastTouched("low")}
                error={isErrThreshold("low", codValue.lowCODAccel)}
              />
              <p>~</p>
              <TextField
                value={codValue.mediumCODAccel}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </div>
            {lastTouched === "low" && isErrThreshold("low", codValue.lowCODAccel) && (
              <p className="threshold-error-message">
                {getErrMessage("low", codValue.lowCODAccel)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="ActionRowCont">
        <div className="row-title-editable">
          <p className="row-title-default-label">{`${t(`cod`)} ${t("effort")} 2`}</p>
          <div className="row-title-label">
            <p>{t("metric.mediumCODAccel")}</p>
          </div>
        </div>

        <div className="ActionRowItemCont">
          <div className="action-threshold">
            <p className="action-form-label">
              {t("threshold")} ({getAccelUnit()})
            </p>
            <div className="jump-threshold-row">
              <TextField
                type="number"
                slotProps={{
                  htmlInput: {
                    step: 0.01,
                  },
                }}
                value={codValue.mediumCODAccel}
                onChange={(e) => {
                  handleChange("mediumCODAccel", Number(e.target.value))
                  if (isErrThreshold("medium", Number(e.target.value))) {
                    setLastTouched("medium")
                  }
                }}
                onBlur={(e) => handleBlur("mediumCODAccel", Number(e.target.value))}
                onFocus={() => setLastTouched("medium")}
                error={isErrThreshold("medium", codValue.mediumCODAccel)}
              />
              <p>~</p>
              <TextField
                value={codValue.highCODAccel}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </div>
            {lastTouched === "medium" && isErrThreshold("medium", codValue.mediumCODAccel) && (
              <p className="threshold-error-message">
                {getErrMessage("medium", codValue.mediumCODAccel)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="ActionRowCont">
        <div className="row-title-editable">
          <p className="row-title-default-label">{`${t(`cod`)} ${t("effort")} 3`}</p>
          <div className="row-title-label">
            <p>{t("metric.highCODAccel")}</p>
          </div>
        </div>

        <div className="ActionRowItemCont">
          <div className="action-threshold">
            <p className="action-form-label">
              {t("threshold")} ({getAccelUnit()})
            </p>
            <div className="jump-threshold-row">
              <TextField
                type="number"
                value={codValue.highCODAccel}
                onChange={(e) => {
                  handleChange("highCODAccel", Number(e.target.value))
                  if (isErrThreshold("high", Number(e.target.value))) {
                    setLastTouched("high")
                  }
                }}
                onBlur={(e) => handleBlur("highCODAccel", Number(e.target.value))}
                onFocus={() => setLastTouched("high")}
                error={isErrThreshold("high", codValue.highCODAccel)}
              />
              <p>~</p>
            </div>
            {lastTouched === "high" && isErrThreshold("high", codValue.highCODAccel) && (
              <p className="threshold-error-message">
                {getErrMessage("high", codValue.highCODAccel)}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
